import React, {
  FunctionComponent,
  useState,
  useEffect,
  useRef,
  ChangeEvent,
} from "react";
import {
  EnrollmentFormProps2021,
  EnrollOptionalLabel,
} from "./../EnrollmentFormPage";
import { FocusableHeader } from "../../../components";
import BackNextButtons from "./../BackNextButtons";
import {
  useTranslate,
  getValidationObject,
  EnrollmentPages,
} from "../../../helpers";
import {
  ChoiceList,
  TextField,
  Alert,
  HelpDrawerToggle,
  HelpDrawer,
  CloseIconThin,
} from "@cmsgov/ds-medicare-gov";
import PlanInfo from "./../PlanInfo";
import {
  PlanType,
  PlanMaterialLanguage,
  AccessibilityFormat,
} from "../../../@types";
import {
  removeExtraWhiteSpace,
  shouldShowDrugCoverageQuestion,
} from "../../../helpers/enrollmentFormHelpers";

export const AccessibilityFormatLabelKeys = {
  [AccessibilityFormat.DEFAULT]: "enroll.questions.2021.default_format",
  [AccessibilityFormat.AUDIO_CD]: "enroll.questions.2021.audio_cd",
  [AccessibilityFormat.BRAILLE]: "enroll.questions.2021.braille",
  [AccessibilityFormat.LARGE_PRINT]: "enroll.questions.2021.large_print",
};

export const PlanMaterialLanguageLabelKeys = {
  [PlanMaterialLanguage.DEFAULT]: "enroll.questions.2021.default_language",
  [PlanMaterialLanguage.SPANISH]: "enroll.questions.2021.spanish",
  [PlanMaterialLanguage.OTHER]: "enroll.questions.2021.other_language",
};

const GeneralQuestions: FunctionComponent<EnrollmentFormProps2021> = ({
  enrollData,
  setEnrollData,
  previousPage,
  nextPage,
  plan,
  inReview,
}) => {
  const t = useTranslate();
  const [isValidForm, setIsValidForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [
    hasAdditionalDrugCoverageErrorMessage,
    setHasAdditionalDrugCoverageErrorMessage,
  ] = useState<string | undefined>(undefined);
  const [
    additionalDrugCoverageNameErrorMessage,
    setAdditionalDrugCoverageNameErrorMessage,
  ] = useState<string | undefined>(undefined);
  const [
    additionalDrugCoverageIdErrorMessage,
    setAdditionalDrugCoverageIdErrorMessage,
  ] = useState<string | undefined>(undefined);
  const [
    additionalDrugCoverageGroupErrorMessage,
    setAdditionalDrugCoverageGroupErrorMessage,
  ] = useState<string | undefined>(undefined);
  const [emailError, setEmailError] = useState<string | undefined>(undefined);
  const alertDiv = useRef<HTMLDivElement>(null);
  const [helpDrugCoverageOpen, setHelpDrugCoverageOpen] = useState(false);
  const [helpEmailMaterialsOpen, setHelpEmailMaterialsOpen] = useState(false);

  const validateForm = (): void => {
    const formStatus = getValidationObject(
      enrollData,
      plan,
      EnrollmentPages.GeneralQuestions
    );

    setIsValidForm(formStatus.isValid);

    if (submitted) {
      if (formStatus.hasAdditionalDrugCoverageValidity) {
        setHasAdditionalDrugCoverageErrorMessage(undefined);
      } else {
        setHasAdditionalDrugCoverageErrorMessage(
          t("enroll_form.additional_drug_coverage.error")
        );
      }
      if (enrollData.hasAdditionalDrugCoverage) {
        if (formStatus.additionalDrugCoverageNameValidity) {
          setAdditionalDrugCoverageNameErrorMessage(undefined);
        } else {
          setAdditionalDrugCoverageNameErrorMessage(
            t("enroll_form.additional_drug_coverage.name.error")
          );
        }
        if (formStatus.additionalDrugCoverageIdValidity) {
          setAdditionalDrugCoverageIdErrorMessage(undefined);
        } else {
          setAdditionalDrugCoverageIdErrorMessage(
            t("enroll_form.additional_drug_coverage.id.error")
          );
        }
        if (formStatus.additionalDrugCoverageGroupValidity) {
          setAdditionalDrugCoverageGroupErrorMessage(undefined);
        } else {
          setAdditionalDrugCoverageGroupErrorMessage(
            t("enroll_form.additional_drug_coverage.group.error")
          );
        }
      }

      if (!formStatus.emailValidity) {
        setEmailError(t("enroll_form.enter_valid_email"));
      } else {
        setEmailError(undefined);
      }
    }
  };

  const submit = (): void => {
    setSubmitted(true);

    if (isValidForm) {
      nextPage();
    } else {
      validateForm();
    }
  };

  useEffect(() => {
    if (submitted && !isValidForm) {
      if (alertDiv.current) {
        alertDiv.current.scrollIntoView();
      }
    }
  }, [submitted]);

  useEffect(() => {
    validateForm();
  }, [enrollData, submitted]);

  const showAdditionalCoverage = shouldShowDrugCoverageQuestion(plan);

  return (
    <div>
      <FocusableHeader
        className="EnrollmentFormPage__header"
        text={t("enrollment_form.general_questions")}
      />

      <div className="ds-u-measure--wide">
        <PlanInfo plan={plan} />

        <span className="ds-u-visibility--screen-reader">
          {t("sr_only_additional_questions_may_appear")}
        </span>

        <div ref={alertDiv}>
          {!isValidForm && submitted && (
            <Alert
              className="ds-u-margin-top--2 enroll-form-error"
              heading={t("enroll_form.needs_your_attention")}
              variation="error"
            >
              {t("enroll_form.complete_required")}
            </Alert>
          )}
        </div>

        {showAdditionalCoverage && (
          <section>
            <ChoiceList
              name="additionalDrugCoverage"
              type="radio"
              choices={[
                {
                  label: t("yes"),
                  value: "yes",
                  checked: enrollData.hasAdditionalDrugCoverage,
                },
                {
                  label: t("no"),
                  value: "no",
                  checked: enrollData.hasAdditionalDrugCoverage === false,
                },
              ]}
              errorMessage={hasAdditionalDrugCoverageErrorMessage}
              onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                setEnrollData({
                  ...enrollData,
                  hasAdditionalDrugCoverage: e.target.value === "yes",
                  additionalDrugCoverage: {
                    name: "",
                    id: "",
                    group: "",
                  },
                });
              }}
              label={t("enroll.questions.2021.other_drug_coverage")}
              hint={
                <HelpDrawerToggle
                  helpDrawerOpen={helpDrugCoverageOpen}
                  showDrawer={(): void => setHelpDrugCoverageOpen(true)}
                  className="MedicareHelpDrawer__hint"
                >
                  {t("enroll.questions.2021.what_other_drug_coverage")}
                </HelpDrawerToggle>
              }
            />

            {helpDrugCoverageOpen && (
              <div className="mct-c-help-drawer">
                <HelpDrawer
                  heading={t("enroll.questions.2021.what_other_drug_coverage")}
                  onCloseClick={(): void => setHelpDrugCoverageOpen(false)}
                  closeButtonText={<CloseIconThin />}
                >
                  <>
                    <p>
                      {t("enroll.questions.2021.other_drug_coverage_includes")}
                    </p>
                    <ul>
                      <li>
                        {t(
                          "enroll.questions.2021.other_drug_coverage_employer"
                        )}
                      </li>
                      <li>
                        {t(
                          "enroll.questions.2021.other_drug_coverage_private_ins"
                        )}
                      </li>
                      <li>
                        {t("enroll.questions.2021.other_drug_coverage_tricare")}
                      </li>
                      <li>
                        {t("enroll.questions.2021.other_drug_coverage_va")}
                      </li>
                      <li>
                        {t("enroll.questions.2021.other_drug_coverage_state")}
                      </li>
                      <li>
                        {t(
                          "enroll.questions.2021.other_drug_coverage_fed_health"
                        )}
                      </li>
                    </ul>
                  </>
                </HelpDrawer>
              </div>
            )}

            {enrollData.hasAdditionalDrugCoverage && (
              <>
                <TextField
                  name="additionalDrugCoverageName"
                  label={t(
                    "enroll.questions.2021.other_drug_coverage_company_name"
                  )}
                  value={enrollData.additionalDrugCoverage.name}
                  errorMessage={additionalDrugCoverageNameErrorMessage}
                  onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                    setEnrollData({
                      ...enrollData,
                      additionalDrugCoverage: {
                        ...enrollData.additionalDrugCoverage,
                        name: e.target.value,
                      },
                    });
                  }}
                  onBlur={(e: ChangeEvent<HTMLInputElement>): void => {
                    setEnrollData({
                      ...enrollData,
                      additionalDrugCoverage: {
                        ...enrollData.additionalDrugCoverage,
                        name: removeExtraWhiteSpace(e.target.value),
                      },
                    });
                  }}
                />
                <TextField
                  name="additionalDrugCoverageId"
                  label={t(
                    "enroll.questions.2021.other_drug_coverage_member_number"
                  )}
                  value={enrollData.additionalDrugCoverage.id}
                  errorMessage={additionalDrugCoverageIdErrorMessage}
                  onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                    setEnrollData({
                      ...enrollData,
                      additionalDrugCoverage: {
                        ...enrollData.additionalDrugCoverage,
                        id: e.target.value,
                      },
                    });
                  }}
                  onBlur={(e: ChangeEvent<HTMLInputElement>): void => {
                    setEnrollData({
                      ...enrollData,
                      additionalDrugCoverage: {
                        ...enrollData.additionalDrugCoverage,
                        id: removeExtraWhiteSpace(e.target.value),
                      },
                    });
                  }}
                />

                <TextField
                  name="additionalDrugCoverageGroup"
                  label={t(
                    "enroll.questions.2021.other_drug_coverage_group_number"
                  )}
                  value={enrollData.additionalDrugCoverage.group}
                  errorMessage={additionalDrugCoverageGroupErrorMessage}
                  onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                    setEnrollData({
                      ...enrollData,
                      additionalDrugCoverage: {
                        ...enrollData.additionalDrugCoverage,
                        group: e.target.value,
                      },
                    });
                  }}
                  onBlur={(e: ChangeEvent<HTMLInputElement>): void => {
                    setEnrollData({
                      ...enrollData,
                      additionalDrugCoverage: {
                        ...enrollData.additionalDrugCoverage,
                        group: removeExtraWhiteSpace(e.target.value),
                      },
                    });
                  }}
                />
              </>
            )}
          </section>
        )}

        <section>
          <ChoiceList
            type="radio"
            label={`${t("enroll.questions.2021.do_you_work")} ${t(
              "enroll_form.optional"
            )}`}
            name="coverageThroughEmployer"
            choices={[
              {
                label: t("yes"),
                value: "yes",
                checked: enrollData.beneWorks,
              },
              {
                label: t("no"),
                value: "no",
                checked: enrollData.beneWorks === false,
              },
            ]}
            onChange={(e: ChangeEvent<HTMLInputElement>): void => {
              setEnrollData({
                ...enrollData,
                beneWorks: e.target.value === "yes",
              });
            }}
          />

          <ChoiceList
            type="radio"
            label={`${t("enroll.questions.2021.does_your_spouse_work")} ${t(
              "enroll_form.optional"
            )}`}
            className="ds-u-margin-top--3"
            name="spouse-employed"
            choices={[
              {
                label: t("yes"),
                value: "yes",
                checked: enrollData.spouseWorks,
              },
              {
                label: t("no"),
                value: "no",
                checked: enrollData.spouseWorks === false,
              },
            ]}
            onChange={(e: ChangeEvent<HTMLInputElement>): void => {
              setEnrollData({
                ...enrollData,
                spouseWorks: e.target.value === "yes",
              });
            }}
          />
        </section>

        {plan.plan_type !== PlanType.PDP && (
          <section>
            <TextField
              name="primaryCareName"
              label={
                <EnrollOptionalLabel
                  label={t("enroll_form.primary_care_name")}
                />
              }
              value={enrollData.primaryCareName}
              onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                setEnrollData({
                  ...enrollData,
                  primaryCareName: e.target.value,
                });
              }}
              onBlur={(e: ChangeEvent<HTMLInputElement>): void => {
                setEnrollData({
                  ...enrollData,
                  primaryCareName: removeExtraWhiteSpace(e.target.value),
                });
              }}
            />
          </section>
        )}

        <section>
          <ChoiceList
            name="emailMaterials"
            type="radio"
            choices={[
              {
                label: t("yes"),
                value: "yes",
                checked: enrollData.emailOptIn,
              },
              {
                label: t("no"),
                value: "no",
                checked: enrollData.emailOptIn === false,
              },
            ]}
            onChange={(e: ChangeEvent<HTMLInputElement>): void => {
              setEnrollData({
                ...enrollData,
                emailOptIn: e.target.value === "yes",
              });
            }}
            label={`${t("enroll.questions.2021.plan_materials_email")} ${t(
              "enroll_form.optional"
            )}`}
            hint={
              <HelpDrawerToggle
                helpDrawerOpen={helpEmailMaterialsOpen}
                showDrawer={(): void => setHelpEmailMaterialsOpen(true)}
                className="MedicareHelpDrawer__hint"
              >
                {t("enroll.questions.2021.what_email_materials")}
              </HelpDrawerToggle>
            }
          />

          {helpEmailMaterialsOpen && (
            <div className="mct-c-help-drawer">
              <HelpDrawer
                heading={t("enroll.questions.2021.what_email_materials")}
                onCloseClick={(): void => setHelpEmailMaterialsOpen(false)}
                closeButtonText={<CloseIconThin />}
              >
                <>
                  <p>{t("enroll.questions.2021.email_materials.include")}</p>
                  <ul>
                    <li>
                      {t(
                        "enroll.questions.2021.email_materials.enrollment_notices"
                      )}
                    </li>
                    <li>
                      {t("enroll.questions.2021.email_materials.appeals")}
                    </li>
                    <li>{t("enroll.questions.2021.email_materials.anoc")}</li>
                    <li>{t("enroll.questions.2021.email_materials.eoc")}</li>
                    <li>
                      {t("enroll.questions.2021.email_materials.pharmacy")}
                    </li>
                    <li>{t("enroll.questions.2021.email_materials.other")}</li>
                  </ul>
                  <p>{t("enroll.questions.2021.email_materials.contact")}</p>
                </>
              </HelpDrawer>
            </div>
          )}

          {enrollData.emailOptIn && (
            <>
              <TextField
                name="emailMaterialsEmailAddress"
                label={t("enroll.questions.2021.email")}
                value={enrollData.email}
                errorMessage={emailError}
                onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                  setEnrollData({
                    ...enrollData,
                    email: e.target.value,
                  });
                }}
                onBlur={(e: ChangeEvent<HTMLInputElement>): void => {
                  setEnrollData({
                    ...enrollData,
                    email: removeExtraWhiteSpace(e.target.value),
                  });
                }}
              />
            </>
          )}
        </section>

        <section>
          <ChoiceList
            type="radio"
            label={`${t("enroll.questions.2021.select_language")} ${t(
              "enroll_form.optional"
            )}`}
            hint={t("enroll.questions.2021.select_language_hint")}
            name="languageForMaterials"
            choices={[
              {
                label: t(
                  PlanMaterialLanguageLabelKeys[PlanMaterialLanguage.DEFAULT]
                ),
                value: PlanMaterialLanguage.DEFAULT,
                checked: enrollData.language === PlanMaterialLanguage.DEFAULT,
              },
              {
                label: t(
                  PlanMaterialLanguageLabelKeys[PlanMaterialLanguage.SPANISH]
                ),
                value: PlanMaterialLanguage.SPANISH,
                checked: enrollData.language === PlanMaterialLanguage.SPANISH,
              },
              {
                label: t(
                  PlanMaterialLanguageLabelKeys[PlanMaterialLanguage.OTHER]
                ),
                value: PlanMaterialLanguage.OTHER,
                checked: enrollData.language === PlanMaterialLanguage.OTHER,
              },
            ]}
            onChange={(e: ChangeEvent<HTMLInputElement>): void => {
              setEnrollData({
                ...enrollData,
                language: e.target.value as PlanMaterialLanguage,
              });
            }}
          />
        </section>

        <section>
          <ChoiceList
            type="radio"
            label={`${t("enroll.questions.2021.what_accessible_format")} ${t(
              "enroll_form.optional"
            )}`}
            hint={t("enroll.questions.2021.what_accessible_format_hint")}
            name="accessibilityFormat"
            choices={[
              {
                label: t(
                  AccessibilityFormatLabelKeys[AccessibilityFormat.DEFAULT]
                ),
                value: AccessibilityFormat.DEFAULT,
                checked:
                  enrollData.accessibilityFormat ===
                  AccessibilityFormat.DEFAULT,
              },
              {
                label: t(
                  AccessibilityFormatLabelKeys[AccessibilityFormat.BRAILLE]
                ),
                value: AccessibilityFormat.BRAILLE,
                checked:
                  enrollData.accessibilityFormat ===
                  AccessibilityFormat.BRAILLE,
              },
              {
                label: t(
                  AccessibilityFormatLabelKeys[AccessibilityFormat.LARGE_PRINT]
                ),
                value: AccessibilityFormat.LARGE_PRINT,
                checked:
                  enrollData.accessibilityFormat ===
                  AccessibilityFormat.LARGE_PRINT,
              },
              {
                label: t(
                  AccessibilityFormatLabelKeys[AccessibilityFormat.AUDIO_CD]
                ),
                value: AccessibilityFormat.AUDIO_CD,
                checked:
                  enrollData.accessibilityFormat ===
                  AccessibilityFormat.AUDIO_CD,
              },
            ]}
            onChange={(e: ChangeEvent<HTMLInputElement>): void => {
              setEnrollData({
                ...enrollData,
                accessibilityFormat: e.target.value as AccessibilityFormat,
              });
            }}
          />
          <p className="ds-u-margin-top--0">
            {t("enroll.questions.2021.other_acc_format")}
          </p>
        </section>

        <BackNextButtons
          inReview={inReview}
          nextPage={submit}
          previousPage={previousPage}
        />
      </div>
    </div>
  );
};

export default GeneralQuestions;
